import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import {
  DeleteAllNotifications,
  DeleteNotification,
  LoadNotifications,
  LoadUnreadNotificationsCount,
  LoadZendeskSsoRequestInfo,
  LoadZendeskSsoRequestInfoSuccess,
  MarkAllNotificationsAsViewed,
  SetNotificationsVisibility,
  StartListeningNotifications,
  StopListeningNotifications,
  ToggleNotificationViewability,
} from '@layout/notifications/store/actions/notifications.actions';
import { NotificationsState } from '@layout/notifications/store/state/notifications.state';
import { ZendeskSsoRequestInfo } from '@core/sso/models/zendesk/zendesk-sso-request-info.interface';
import { NotificationSource } from '@layout/notifications/models/notification-source.enum';

@Injectable({ providedIn: 'root' })
export class NotificationsFacade {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  readonly notifications = this.store.selectSignal(NotificationsState.notifications);
  readonly areNotificationsVisible = this.store.selectSignal(NotificationsState.areNotificationsVisible);
  readonly unreadNotificationsCount = this.store.selectSignal(NotificationsState.unreadNotificationsCount);
  readonly loading = this.store.selectSignal(NotificationsState.loading);

  loadUnreadNotificationsCount(): void {
    this.store.dispatch(new LoadUnreadNotificationsCount());
  }

  loadNotifications(): void {
    this.store.dispatch(new LoadNotifications());
  }

  setNotificationsVisibility(areNotificationsVisible: boolean): void {
    this.store.dispatch(new SetNotificationsVisibility(areNotificationsVisible));
  }

  startListeningNotifications(): void {
    this.store.dispatch(new StartListeningNotifications());
  }

  stopListeningNotifications(): void {
    this.store.dispatch(new StopListeningNotifications());
  }

  loadZendeskSsoRequestInfo(ticketId: string): void {
    this.store.dispatch(new LoadZendeskSsoRequestInfo(ticketId));
  }

  markAllNotificationsAsViewed(): void {
    this.store.dispatch(new MarkAllNotificationsAsViewed());
  }

  toggleNotificationViewability(notificationId: string, source: NotificationSource, viewed: boolean): void {
    this.store.dispatch(new ToggleNotificationViewability(notificationId, source, viewed));
  }

  deleteAllNotifications(): void {
    this.store.dispatch(new DeleteAllNotifications());
  }

  deleteNotification(notificationId: string, source: NotificationSource): void {
    this.store.dispatch(new DeleteNotification(notificationId, source));
  }

  get onLoadZendeskSsoRequestInfoSuccess$(): Observable<ZendeskSsoRequestInfo> {
    return this.actions$
      .pipe(ofActionDispatched(LoadZendeskSsoRequestInfoSuccess))
      .pipe(map(({ zendeskSsoRequestInfo }) => zendeskSsoRequestInfo));
  }
}
